import React, { useEffect, useState } from "react";
import { Table, Tabs, Button, Input, Select, Tag, Space, Typography, Modal, Form, InputNumber } from "antd";
import { Search, Filter, Upload, Edit, Trash2 } from "lucide-react";
import useRequest from "../hooks/useRequest";
import apiPath from "../constants/apiPath";

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const RewardPoint = () => {
  const [activeTab, setActiveTab] = useState("1");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [form] = Form.useForm();
  const { request } = useRequest();
  const [open, setOpen] = useState(false);

  const fallbackDataSource = [
    {
      key: "1",
      id: 1,
      sku: "shoes400",
      name: "AquaStride Men's Casual Sports Shoes",
      points: 1499,
      status: "Active",
    },
    {
      key: "2",
      id: 2,
      sku: "0079",
      name: "Artificial green plant",
      points: 120,
      status: "Active",
    },
  ];

  const normalizeOrderPayload = (data) => {
    const payload = data?.data ?? data;
    if (Array.isArray(payload)) return payload;
    return payload?.orders ?? payload?.results ?? payload?.data ?? [];
  };

  const getProductPrice = (item) => {
    const rawPrice =
      item?.price ??
      item?.sale_price ??
      item?.salePrice ??
      item?.regular_price ??
      item?.regularPrice ??
      item?.selling_price ??
      item?.sellingPrice ??
      item?.mrp ??
      item?.unit_price ??
      item?.amount ??
      item?.subtotal ??
      item?.sub_total ??
      item?.subtotal_amount ??
      item?.line_total ??
      item?.line_total_amount ??
      item?.total ??
      item?.total_price;

    const numericPrice = Number(rawPrice);
    return Number.isFinite(numericPrice) ? numericPrice : 0;
  };

  const extractRowsFromOrders = (orders) => {
    const rows = [];

    orders.forEach((order, orderIndex) => {
      const items = order?.items ?? order?.order_items ?? [];

      items.forEach((item, itemIndex) => {
        const product = item?.product ?? item;
        const id = product?.id ?? item?.product_source_id ?? item?.product_id ?? item?.id ?? `${orderIndex + 1}-${itemIndex + 1}`;
        const sku = product?.sku ?? product?.product_sku ?? item?.sku ?? item?.product_sku ?? item?.product_source_id ?? "N/A";
        const name = product?.name ?? product?.title ?? item?.name ?? item?.product_name ?? item?.product_name ?? "Unnamed Product";

        rows.push({
          key: `${order?.order_number || order?.id || orderIndex}-${itemIndex}`,
          id,
          sku,
          name,
          points: getProductPrice(item),
          status: product?.status ?? item?.status ?? order?.status ?? "Active",
        });
      });
    });

    return rows;
  };

  const loadOrders = () => {
    request({
      url: apiPath.orders,
      method: "GET",
      onSuccess: (response) => {
        const payload = normalizeOrderPayload(response);
        const rows = extractRowsFromOrders(payload);

        setTableData(rows.length ? rows : fallbackDataSource);
      },
      onError: () => {
        setTableData(fallbackDataSource);
      },
    });
  };

  useEffect(() => {
    loadOrders();
  }, []);

  const handleAddReward = (values) => {
    const newId = tableData.length > 0 ? Math.max(...tableData.map((item) => Number(item.id) || 0)) + 1 : 1;
    const newItem = {
      key: String(newId),
      id: newId,
      sku: values.sku,
      name: values.name,
      points: values.points,
      status: values.status || "Active",
    };

    setTableData((prev) => [newItem, ...prev]);
    setIsModalOpen(false);
    form.resetFields();
  };

  const columns = [
    {
      title: "Id",
      dataIndex: "id",
      key: "id",
      align: "center",
    },
    {
      title: "SKU",
      dataIndex: "sku",
      key: "sku",
      align: "center",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "35%",
      render: (text) => <span style={{ color: "#64748b" }}>{text}</span>,
    },
    {
      title: "Points",
      dataIndex: "points",
      key: "points",
      align: "center",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (status) => (
        <Tag
          color="success"
          style={{
            borderRadius: "12px",
            padding: "2px 12px",
            fontWeight: 500,
            background: "#10b981",
            color: "#fff",
            border: "none",
          }}
        >
          {status}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: () => (
        <Space size="middle">
          {/* <Button type="text" icon={<Edit size={16} color="#64748b" />} style={{ minWidth: "auto" }} /> */}
          <Button type="text" onClick={() => setOpen(true)} icon={<Trash2 size={16} color="#64748b" />} style={{ minWidth: "auto" }} />

        </Space>
      ),
    },
  ];

  return (
    <div className="reward-point-container" style={{ padding: "24px", background: "#fff", minHeight: "100vh" }}>
      <>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
          <Title level={4} style={{ margin: 0, color: "#0f172a", fontWeight: 600 }}>
            Points On Product
          </Title>
          <Space size="middle">
            <Button
              icon={<Upload size={16} />}
              style={{ display: "flex", alignItems: "center", borderRadius: "6px", color: "#64748b", fontWeight: 500, border: "none", boxShadow: "none" }}
            >
              Export
            </Button>
            <Button
              type="primary"
              style={{ borderRadius: "6px", background: "#2563eb", fontWeight: 500 }}
              onClick={() => setIsModalOpen(true)}
            >
              Create Product Points
            </Button>
          </Space>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <Space size="large">
            <Input
              placeholder="Search"
              suffix={<Search size={16} color="#94a3b8" />}
              style={{ width: 250, borderRadius: "6px" }}
            />
          </Space>
        </div>

        <Table scroll={{ x: 'max-content' }}
          columns={columns}
          dataSource={tableData}
          pagination={true}
          style={{ border: "1px solid #f1f5f9", borderRadius: "8px" }}
          className="reward-table-custom"
        />
      </>
      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => {
          console.log("Deleted!");
          setOpen(false);
        }}
        okText="Delete"
        cancelText="Cancel"
        title="Are you sure?"
      >
        <p>Are you sure you want to delete this Product?</p>
      </Modal>
      <Modal
        title="Create Product Reward"
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <div style={{ padding: "20px 0" }}>
          <Form form={form} layout="vertical" onFinish={handleAddReward} initialValues={{ status: "Active" }}>
            <Form.Item label="Name" name="name" rules={[{ required: true, message: "Please enter product name" }]}>
              <Input placeholder="Enter product name" />
            </Form.Item>
            <Form.Item label="SKU" name="sku" rules={[{ required: true, message: "Please enter SKU" }]}>
              <Input placeholder="Enter SKU" />
            </Form.Item>
            <Form.Item label="Points" name="points" rules={[{ required: true, message: "Please enter points" }]}>
              <InputNumber min={0} style={{ width: "100%" }} placeholder="Enter points" />
            </Form.Item>
            <Form.Item label="Status" name="status">
              <Select>
                <Option value="Active">Active</Option>
                <Option value="Inactive">Inactive</Option>
              </Select>
            </Form.Item>
            <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
              <Space>
                <Button
                  onClick={() => {
                    setIsModalOpen(false);
                    form.resetFields();
                  }}
                >
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit" style={{ background: "#2563eb" }}>
                  Create
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </div>
      </Modal>
    </div>
  );
};

export default RewardPoint;
